import { useEffect, useState } from 'react';
import { ApiError, api, type ParamDef, type RunResult, type User } from '../api';
import { Banner, Pill, Spinner, useAsync } from '../components/Common';
import { ParamForm, defaultParamValues } from '../components/ParamForm';
import { BlockRenderer } from '../components/Blocks';
import { EmailForm } from '../components/EmailForm';
import { formatDateTime } from '../format';

interface ReportDetail {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  params: ParamDef[];
}

/**
 * The manager-facing screen. One filter row, then blocks, then exports.
 *
 * Exports link to the stored run rather than re-running the report, so the
 * spreadsheet and the PDF are provably the same figures as the screen.
 */
export function RunReport({ slug, runId, user }: { slug?: string; runId?: string; user: User }) {
  const [values, setValues] = useState<Record<string, any> | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const report = useAsync(
    () => (slug ? api.get<ReportDetail>(`/api/reports/${slug}`) : Promise.resolve(null)),
    [slug],
  );

  // Opening a stored run (from history or an emailed link) shows exactly what was
  // generated then — not a fresh query that may disagree with the PDF someone is
  // holding.
  useEffect(() => {
    if (!runId) return;
    setRunning(true);
    api
      .get<RunResult>(`/api/runs/${runId}`)
      .then(setResult)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setRunning(false));
  }, [runId]);

  useEffect(() => {
    if (report.data && values === null) setValues(defaultParamValues(report.data.params));
  }, [report.data]);

  const run = async (refresh = false) => {
    if (!report.data || !values) return;
    setRunning(true);
    setError(null);
    setErrorList([]);
    try {
      const next = await api.post<RunResult>(`/api/reports/${report.data.slug}/run`, { params: values, refresh });
      setResult(next);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorList(Array.isArray(err.detail) ? (err.detail as string[]) : []);
      } else {
        setError(String(err));
      }
    } finally {
      setRunning(false);
    }
  };

  if (report.loading && !report.data && !runId) return <Spinner label="Loading report…" />;
  if (report.error) return <Banner tone="error" title="Could not open this report. ">{report.error}</Banner>;

  const title = report.data?.name ?? result?.reportName ?? 'Report';
  const canRefresh = user.role === 'admin' || user.role === 'editor';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          {report.data?.description && <p>{report.data.description}</p>}
        </div>
        {result && (
          <div className="page-actions">
            <a className="btn" href={`/api/runs/${result.runId}/export.xlsx`}>
              Excel
            </a>
            <a className="btn" href={`/api/runs/${result.runId}/export.pdf`}>
              PDF
            </a>
            <a className="btn" href={`/api/runs/${result.runId}/print`} target="_blank" rel="noreferrer">
              Print view
            </a>
            <button className="btn primary" onClick={() => setShowEmail((v) => !v)}>
              {showEmail ? 'Close' : 'Email'}
            </button>
          </div>
        )}
      </div>

      {/*
        Emails THIS run -- the stored one, by id -- so what lands in the inbox is
        provably the figures on screen, not a re-query a minute later. The body
        IS the report: the headline figures and every table, laid out for a
        mail client. Charts and the notes are left to the attachments -- a
        "Calls by person" bar chart inlined into an email is a screenshot that
        gets forwarded without its scale, and it does not fit a phone.
      */}
      {result && showEmail ? (
        <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Email this report</h3>
          <EmailForm
            defaultAttachments={['pdf']}
            onSend={async ({ to, note, formats }) => {
              const res = await api.post<{ detail: string }>(`/api/runs/${result.runId}/email`, { to, note, formats });
              return res.detail;
            }}
            explain={
              <>
                The email carries the headline figures and every table. Charts and the notes are only in the
                attached PDF, so the body stays readable on a phone.
              </>
            }
          />
        </div>
      ) : null}

      {report.data && values && (
        <div className="filter-bar">
          <ParamForm defs={report.data.params} values={values} onChange={setValues} disabled={running} />
          <div className="filter-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="primary" onClick={() => run(false)} disabled={running}>
              {running ? 'Running…' : result ? 'Run again' : 'Run report'}
            </button>
            {canRefresh && (
              <button
                onClick={() => run(true)}
                disabled={running}
                title="Ignore cached upstream responses and re-query the source systems. Uses part of the Dialpad hourly quota."
              >
                Refresh data
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <Banner tone="error" title="The report could not run. " items={errorList.length > 1 ? errorList : undefined}>
          {errorList.length > 1 ? null : error}
        </Banner>
      )}

      {running && !result && <Spinner label="Querying the source systems…" />}

      {result && (
        <div className={running ? 'stale' : undefined}>
          <dl className="dl">
            {result.paramLabels.map((p) => (
              <div key={p.name}>
                <dt>{p.label}</dt>
                <dd>{p.display}</dd>
              </div>
            ))}
            <div>
              <dt>Generated</dt>
              <dd>{formatDateTime(result.generatedAt)}</dd>
            </div>
            <div>
              <dt>Run by</dt>
              <dd>{result.generatedBy}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                <Pill tone={result.status === 'ok' ? 'ok' : result.status === 'partial' ? 'warn' : 'bad'}>
                  {result.status === 'ok' ? 'Complete' : result.status === 'partial' ? 'Partial data' : 'Failed'}
                </Pill>
              </dd>
            </div>
          </dl>

          {result.status === 'error' && (
            <Banner tone="error" title="This run failed and the figures below are incomplete. " items={result.warnings} />
          )}

          {result.accuracyNotes.length > 0 && (
            <Banner tone="warn" title="Read before acting on these figures" items={result.accuracyNotes} />
          )}

          {result.status !== 'error' && result.warnings.length > 0 && (
            <Banner tone="info" title="Notes from this run" items={result.warnings} />
          )}

          {result.blocks.map((block, i) => (
            <BlockRenderer key={block.id ?? i} block={block} data={result.data} />
          ))}

          <section className="card">
            <button className="ghost small" onClick={() => setShowDiagnostics((v) => !v)} aria-expanded={showDiagnostics}>
              {showDiagnostics ? '▾ Hide data sources' : '▸ Where these figures came from'}
            </button>
            {showDiagnostics && (
              <div className="table-wrap" style={{ marginTop: 10 }}>
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Upstream call</th>
                      <th className="right">Rows</th>
                      <th className="right">Time</th>
                      <th>Freshness</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.diagnostics.map((d) => (
                      <tr key={d.key}>
                        <td>{d.key}</td>
                        <td className="mono">{d.target ?? '—'}</td>
                        <td className="right">{d.rowCount}</td>
                        <td className="right">{d.durationMs} ms</td>
                        <td>{d.cached ? <Pill tone="warn">cached</Pill> : <Pill tone="ok">live</Pill>}</td>
                        <td>
                          {d.status === 'ok' ? <Pill tone="ok">ok</Pill> : <Pill tone="bad">{d.error ?? 'error'}</Pill>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="note">
                  “Cached” means the upstream response was reused rather than re-fetched. Dialpad is cached for an hour by
                  design — its Stats API allows only 200 requests per hour.
                </p>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
